import { useEffect, useState } from "react";
import { FileSpreadsheet, Loader2, Download, Info, ShieldX, AlertTriangle } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { api, apiError } from "@/lib/api";
import { mxn } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { PolizaCard } from "@/components/finanzas/PolizaCard";
import { toast } from "sonner";

function thisMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

// Formatea "YYYY-MM" a "mes YYYY" en español (para el mensaje de sugerencia).
function periodoLegible(per) {
  if (!/^\d{4}-\d{2}$/.test(per || "")) return per;
  const [y, m] = per.split("-");
  const meses = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
    "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
  return `${meses[parseInt(m, 10) - 1]} de ${y}`;
}

export default function ReportesFiscales() {
  const [periodo, setPeriodo] = useState(thisMonth());
  const [mesActividad, setMesActividad] = useState({ mes_mas_reciente: null, tiene_movimientos: false });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cobrado, setCobrado] = useState(null);
  const [polizas, setPolizas] = useState(null);
  const [iva, setIva] = useState(null);
  const [isr, setIsr] = useState(null);
  const [inputs, setInputs] = useState({ iva_acreditable: 0, coeficiente_utilidad: "", pagos_provisionales_previos: 0 });
  const [savingIva, setSavingIva] = useState(false);
  const [savingIsr, setSavingIsr] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = async (per) => {
    setLoading(true);
    try {
      const [c, p, vi, sr, inp] = await Promise.all([
        api.get(`/fiscal/cobrado?periodo=${per}`).then((r) => r.data),
        api.get(`/fiscal/polizas?periodo=${per}`).then((r) => r.data),
        api.get(`/fiscal/precalculo/iva?periodo=${per}`).then((r) => r.data),
        api.get(`/fiscal/precalculo/isr?periodo=${per}`).then((r) => r.data),
        api.get(`/fiscal/precalculo/inputs?periodo=${per}`).then((r) => r.data),
      ]);
      setCobrado(c); setPolizas(p); setIva(vi); setIsr(sr);
      setInputs({
        iva_acreditable: inp.iva_acreditable ?? 0,
        coeficiente_utilidad: inp.coeficiente_utilidad ?? "",
        pagos_provisionales_previos: inp.pagos_provisionales_previos ?? 0,
      });
      setError(false);
    } catch (e) {
      if (e?.response?.status === 403) setError(true);
      else toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (/^\d{4}-\d{2}$/.test(periodo)) load(periodo); /* eslint-disable-next-line */ }, [periodo]);

  // Al montar: consulta mes con actividad más reciente y ajusta el periodo default.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/fiscal/mes-actividad");
        setMesActividad(r.data || { mes_mas_reciente: null, tiene_movimientos: false });
        if (r.data?.mes_mas_reciente && /^\d{4}-\d{2}$/.test(r.data.mes_mas_reciente)) {
          setPeriodo(r.data.mes_mas_reciente);
        }
      } catch { /* silencioso: si falla queda thisMonth() */ }
    })();
  }, []);

  const guardarInputs = async (setSaving) => {
    setSaving(true);
    try {
      await api.put(`/fiscal/precalculo/inputs?periodo=${periodo}`, {
        iva_acreditable: Number(inputs.iva_acreditable) || 0,
        coeficiente_utilidad: inputs.coeficiente_utilidad === "" ? null : Number(inputs.coeficiente_utilidad),
        pagos_provisionales_previos: Number(inputs.pagos_provisionales_previos) || 0,
      });
      const [vi, sr] = await Promise.all([
        api.get(`/fiscal/precalculo/iva?periodo=${periodo}`).then((r) => r.data),
        api.get(`/fiscal/precalculo/isr?periodo=${periodo}`).then((r) => r.data),
      ]);
      setIva(vi); setIsr(sr);
      toast.success("Pre-cálculo actualizado");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const exportar = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/fiscal/export?periodo=${periodo}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `fiscal_${periodo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte XLSX descargado");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="reportes-fiscal-error">
        <ShieldX className="h-10 w-10 text-red-400" />
        <p className="mt-3 text-sm text-slate-500">No tienes acceso a los reportes de Contabilidad y Fiscal.</p>
      </div>
    );
  }

  const ic = cobrado?.ingresos_cobrados;
  const ivaCob = cobrado?.iva_trasladado_cobrado;

  return (
    <div data-testid="reportes-fiscal-page">
      <PageHeader title="Reportes y pólizas fiscales" subtitle="Ingresos cobrados (flujo de efectivo) y pólizas sugeridas por periodo — SOFOM ENR">
        <div className="flex items-end gap-2">
          <div>
            <Label className="text-[11px] text-slate-500">Periodo</Label>
            <Input data-testid="periodo-input" type="month" value={periodo} className="h-9 w-40"
              onChange={(e) => setPeriodo(e.target.value)} />
          </div>
          <Button data-testid="exportar-xlsx-btn" size="sm" className="bg-primary hover:bg-sidebar-hover" onClick={exportar} disabled={exporting || loading}>
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Exportar XLSX
          </Button>
        </div>
      </PageHeader>

      {loading ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : (
        <div className="p-6 md:p-8">
          <p className="mb-4 flex items-center gap-1.5 text-xs text-slate-500" data-testid="fiscal-leyenda">
            <Info className="h-3.5 w-3.5" /> {cobrado?.leyenda}
          </p>

          {/* Tarjetas resumen */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <ResumenCard testid="card-ingresos" label="Total ingresos cobrados" value={ic?.total_ingresos} accent="text-primary" />
            <ResumenCard testid="card-iva" label="IVA trasladado cobrado" value={ivaCob?.total_iva} accent="text-amber-600" />
            <ResumenCard testid="card-comision" label="Comisión apertura cobrada" value={ic?.comision_apertura_cobrada} accent="text-slate-800" />
            <ResumenCard testid="card-capital" label="Capital recuperado (informativo)" value={cobrado?.capital_recuperado_informativo} accent="text-slate-500" />
          </div>

          <Tabs defaultValue="cobrado" className="mt-6">
            <TabsList data-testid="reportes-tabs">
              <TabsTrigger value="cobrado" data-testid="tab-cobrado">Cobrado</TabsTrigger>
              <TabsTrigger value="polizas" data-testid="tab-polizas">Pólizas ({polizas?.total_polizas ?? 0})</TabsTrigger>
              <TabsTrigger value="excluidos" data-testid="tab-excluidos">Excluidos ({cobrado?.excluidos?.length ?? 0})</TabsTrigger>
              <TabsTrigger value="iva" data-testid="tab-iva">IVA</TabsTrigger>
              <TabsTrigger value="isr" data-testid="tab-isr">ISR</TabsTrigger>
            </TabsList>

            {/* ---- Cobrado ---- */}
            <TabsContent value="cobrado" className="mt-4">
              <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3 text-sm">
                <Linea label="Interés cobrado" value={ic?.interes_cobrado} />
                <Linea label="Moratorio cobrado" value={ic?.moratorio_cobrado} />
                <Linea label="IVA interés" value={ivaCob?.iva_interes} />
                <Linea label="IVA comisión" value={ivaCob?.iva_comision} />
                <Linea label="IVA moratorio" value={ivaCob?.iva_moratorio} />
              </div>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <Table data-testid="tabla-cobrado">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recibo</TableHead><TableHead>Fecha</TableHead><TableHead>Crédito</TableHead>
                      <TableHead className="text-right">Monto</TableHead><TableHead className="text-right">Capital</TableHead>
                      <TableHead className="text-right">Interés</TableHead><TableHead className="text-right">IVA</TableHead>
                      <TableHead className="text-right">Moratorio</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(cobrado?.detalle_pagos || []).map((d) => (
                      <TableRow key={d.recibo} data-testid={`cobrado-row-${d.recibo}`}>
                        <TableCell className="font-mono text-xs">{d.recibo}</TableCell>
                        <TableCell className="text-xs">{d.fecha_pago}</TableCell>
                        <TableCell className="text-xs">{d.numero_credito || "—"}</TableCell>
                        <TableCell className="text-right tabular-nums">{mxn(d.monto)}</TableCell>
                        <TableCell className="text-right tabular-nums">{mxn(d.capital)}</TableCell>
                        <TableCell className="text-right tabular-nums">{mxn(d.interes)}</TableCell>
                        <TableCell className="text-right tabular-nums">{mxn(d.iva)}</TableCell>
                        <TableCell className="text-right tabular-nums">{mxn(d.moratorio)}</TableCell>
                      </TableRow>
                    ))}
                    {(cobrado?.detalle_pagos || []).length === 0 && (
                      <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-slate-400">Sin pagos cobrados en el periodo.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* ---- Pólizas ---- */}
            <TabsContent value="polizas" className="mt-4">
              <p className="mb-3 flex items-center gap-1.5 text-xs text-slate-500">
                <Info className="h-3.5 w-3.5" /> {polizas?.leyenda}
              </p>
              {(polizas?.total_polizas_finanzas ?? 0) > 0 && (
                <p data-testid="polizas-desglose-origen" className="mb-3 text-xs text-slate-600">
                  {polizas.total_polizas_credito} del ciclo de crédito ·{" "}
                  <span className="font-medium text-violet-800">
                    {polizas.total_polizas_finanzas} de Administración financiera
                  </span>{" "}
                  (capital, fondeo y gastos)
                </p>
              )}
              <div className="space-y-4">
                {(polizas?.polizas || []).map((pz, i) => (
                  <PolizaCard key={`${pz.referencia}-${i}`} pz={pz} periodo={periodo} />
                ))}
                {(polizas?.polizas || []).length === 0 && (
                  <p data-testid="polizas-empty-msg" className="py-8 text-center text-sm text-slate-400">
                    {!mesActividad.tiene_movimientos
                      ? "Aún no hay movimientos contables registrados."
                      : mesActividad.mes_mas_reciente && mesActividad.mes_mas_reciente !== periodo
                        ? `Sin movimientos en ${periodoLegible(periodo)}. La actividad más reciente fue en ${periodoLegible(mesActividad.mes_mas_reciente)} — prueba ese periodo.`
                        : "Sin pólizas sugeridas en el periodo."}
                  </p>
                )}
              </div>
            </TabsContent>

            {/* ---- Excluidos ---- */}
            <TabsContent value="excluidos" className="mt-4">
              <p className="mb-3 text-xs text-slate-500">Pagos NO sumados al cobrado (visibles para trazabilidad de qué quedó fuera y por qué).</p>
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
                <Table data-testid="tabla-excluidos">
                  <TableHeader>
                    <TableRow><TableHead>Recibo</TableHead><TableHead>Fecha</TableHead><TableHead className="text-right">Monto</TableHead><TableHead>Motivo</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {(cobrado?.excluidos || []).map((x) => (
                      <TableRow key={x.recibo} data-testid={`excluido-row-${x.recibo}`}>
                        <TableCell className="font-mono text-xs">{x.recibo}</TableCell>
                        <TableCell className="text-xs">{x.fecha_pago || "(vacía)"}</TableCell>
                        <TableCell className="text-right tabular-nums">{mxn(x.monto)}</TableCell>
                        <TableCell className="text-xs text-amber-700">{x.motivo}</TableCell>
                      </TableRow>
                    ))}
                    {(cobrado?.excluidos || []).length === 0 && (
                      <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-slate-400">Sin pagos excluidos en el periodo.</TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* ---- IVA ---- */}
            <TabsContent value="iva" className="mt-4">
              <LeyendaFiscal texto={iva?.leyenda} />
              <div className="mt-4 max-w-xl rounded-lg border border-slate-200 bg-white p-6" data-testid="panel-iva">
                <div className="space-y-3 text-sm">
                  <Linea label="IVA trasladado cobrado (Fase B)" value={iva?.iva_trasladado_cobrado} />
                  <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 px-3 py-2">
                    <span className="text-slate-500">IVA acreditable (captura manual)</span>
                    <MoneyInput data-testid="input-iva-acreditable" className="h-8 w-40 text-right"
                      value={inputs.iva_acreditable}
                      onChange={(v) => setInputs({ ...inputs, iva_acreditable: v })} />
                  </div>
                  <div className="flex items-center justify-between rounded-md border-2 border-primary/30 bg-primary/5 px-3 py-2.5 font-semibold">
                    <span>IVA {iva?.resultado_tipo}</span>
                    <span data-testid="iva-resultado" className="tabular-nums text-primary">{mxn(iva?.resultado || 0)}</span>
                  </div>
                  <p className="text-[11px] text-slate-400">Sin DIOT (fuera de alcance). El IVA acreditable se captura manualmente (no hay compras registradas en el sistema).</p>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button data-testid="guardar-iva-btn" size="sm" className="bg-primary hover:bg-sidebar-hover" onClick={() => guardarInputs(setSavingIva)} disabled={savingIva}>
                    {savingIva && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar y recalcular
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ---- ISR ---- */}
            <TabsContent value="isr" className="mt-4">
              <LeyendaFiscal texto={isr?.leyenda} />
              <div className="mt-4 max-w-xl rounded-lg border border-slate-200 bg-white p-6" data-testid="panel-isr">
                <div className="space-y-3 text-sm">
                  <Linea label="Ingresos cobrados del periodo" value={isr?.ingresos_periodo} />
                  <Linea label="Ingresos cobrados acumulados (ejercicio)" value={isr?.ingresos_acumulados} />
                  <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 px-3 py-2">
                    <span className="text-slate-500">Coeficiente de utilidad (ej. 0.30)</span>
                    <Input data-testid="input-coeficiente" type="number" step="0.0001" className="h-8 w-40 text-right"
                      value={inputs.coeficiente_utilidad}
                      onChange={(e) => setInputs({ ...inputs, coeficiente_utilidad: e.target.value })} />
                  </div>
                  {!isr?.coeficiente_disponible && (
                    <p className="text-[11px] text-amber-600">Captura el coeficiente de utilidad para estimar el ISR (también puede definirse en Configuración fiscal).</p>
                  )}
                  <Linea label="Utilidad fiscal estimada" value={isr?.utilidad_estimada} />
                  <Linea label={`ISR causado (tasa ${isr?.tasa_isr ?? 30}%)`} value={isr?.isr_causado} />
                  <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 px-3 py-2">
                    <span className="text-slate-500">Pagos provisionales previos (captura)</span>
                    <MoneyInput data-testid="input-pagos-previos" className="h-8 w-40 text-right"
                      value={inputs.pagos_provisionales_previos}
                      onChange={(v) => setInputs({ ...inputs, pagos_provisionales_previos: v })} />
                  </div>
                  <div className="flex items-center justify-between rounded-md border-2 border-primary/30 bg-primary/5 px-3 py-2.5 font-semibold">
                    <span>ISR provisional {isr?.resultado_tipo}</span>
                    <span data-testid="isr-resultado" className="tabular-nums text-primary">{mxn(isr?.isr_provisional || 0)}</span>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <Button data-testid="guardar-isr-btn" size="sm" className="bg-primary hover:bg-sidebar-hover" onClick={() => guardarInputs(setSavingIsr)} disabled={savingIsr}>
                    {savingIsr && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />} Guardar y recalcular
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      )}
    </div>
  );
}

function LeyendaFiscal({ texto }) {
  return (
    <div data-testid="leyenda-fiscal" className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="font-medium">{texto || "Cálculo de apoyo, sujeto a revisión del contador responsable. NO constituye declaración fiscal."}</span>
    </div>
  );
}

function ResumenCard({ testid, label, value, accent }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-slate-200 bg-white p-4">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`mt-1 font-heading text-xl font-bold tabular-nums ${accent}`}>{mxn(value || 0)}</p>
    </div>
  );
}

function Linea({ label, value }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-slate-100 px-3 py-2">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium tabular-nums">{mxn(value || 0)}</span>
    </div>
  );
}
